// Plan 24c, Task 7 — Pummel Awards screen (Screen 24)
//
// Full-viewport overlay that displays the post-game awards ceremony.
// Sequentially reveals each award category + winner using the AwardsCard
// primitive, with a configurable hold duration between reveals.
// Navigates to 'game-over' once all awards have been shown (or immediately on
// skip / ESC).

import { createAwardsCard } from '../components/awards-card';
import { createButton } from '../components/button';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import type { AwardCategoryId } from '@shared/game/types';

export type { AwardCategoryId };

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type AwardEntry = {
  categoryId: AwardCategoryId;
  /** Display label, e.g. 'MOST KEYS GAINED' */
  categoryLabel: string;
  winnerPlayerId: string;
  statValue: number;
  /** e.g. 'keys', 'damage', 'wins' */
  statSuffix: string;
};

export type PummelAwardsPayload = BoardMountPayload & {
  /** 1–6 award entries to reveal sequentially. */
  awards: AwardEntry[];
  /** Seconds to hold each award before advancing. Default: 3.0 */
  perAwardHoldSeconds?: number;
  /** Called after the last award is revealed. Default: navigate to 'game-over'. */
  onAllRevealed?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants
// ─────────────────────────────────────────────────────────────────────────────

const Z_INDEX_OVERLAY = 350;
const OVERLAY_BG = 'rgba(0, 0, 0, 0.60)';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createPummelAwardsScreen(
  ctx: ScreenMountContext<PummelAwardsPayload>,
): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { state, awards, onAllRevealed } = payload;
  const perAwardHoldSeconds = payload.perAwardHoldSeconds ?? 3.0;

  // ── Validation — BEFORE creating any DOM ────────────────────────────────────

  if (awards.length === 0) {
    throw new Error('pummel-awards: awards must have at least 1 entry');
  }
  if (awards.length > 6) {
    throw new Error('pummel-awards: max 6 categories');
  }

  let disposed = false;

  // ── doAllRevealed ──────────────────────────────────────────────────────────

  const doAllRevealed = (): void => {
    if (onAllRevealed) {
      onAllRevealed();
    } else {
      router.navigate('game-over', payload);
    }
  };

  // ── Root overlay ────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-pummel-awards';
  root.setAttribute('data-screen-id', 'pummel-awards');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_OVERLAY}`,
    `background: ${OVERLAY_BG}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
  ].join('; ');

  // ── Centered container ─────────────────────────────────────────────────────

  const centeredContainer = document.createElement('div');
  centeredContainer.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: center',
  ].join('; ');

  // ── Awards card ────────────────────────────────────────────────────────────
  // The card already renders its own "🏆 PUMMEL AWARDS 🏆" title — no extra title needed.

  const awardsCardHandle = createAwardsCard({
    gsapRegistry,
    mount: centeredContainer,
  });

  root.appendChild(centeredContainer);

  // ── Sequential reveal timeline ─────────────────────────────────────────────

  const seqHandle = gsapRegistry.create('pummel-awards:sequence');

  for (let i = 0; i < awards.length; i++) {
    const award = awards[i];
    const position = i * perAwardHoldSeconds;

    seqHandle.timeline.call(
      () => {
        if (disposed) return;
        awardsCardHandle.reveal({
          category: award.categoryLabel,
          winnerName: state.players.get(award.winnerPlayerId)?.name ?? 'Unknown',
        });
      },
      undefined,
      position,
    );
  }

  // After last reveal: navigate / call onAllRevealed
  seqHandle.timeline.call(
    () => {
      if (disposed) return;
      doAllRevealed();
    },
    undefined,
    awards.length * perAwardHoldSeconds,
  );

  // ── Skip button ────────────────────────────────────────────────────────────

  const skipMount = document.createElement('div');
  skipMount.style.cssText = ['position: absolute', 'bottom: 24px', 'right: 24px'].join('; ');

  const skipButtonHandle = createButton({
    gsapRegistry,
    variant: 'ghost',
    size: 'small',
    label: 'Skip Awards',
    onClick: () => {
      if (!disposed) {
        seqHandle.dispose();
        doAllRevealed();
      }
    },
  });
  skipMount.appendChild(skipButtonHandle.element);

  root.appendChild(skipMount);

  // ── ESC handler ────────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (!disposed) {
      seqHandle.dispose();
      doAllRevealed();
    }
  }

  document.addEventListener('keydown', handleEsc);

  host.appendChild(root);

  // ── dispose() ─────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    seqHandle.dispose();
    awardsCardHandle.dispose();
    skipButtonHandle.dispose();
    document.removeEventListener('keydown', handleEsc);

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
